import { useShowToast } from '@/hooks';
import { useQuery, useMutation } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { useState, useMemo, useEffect, useRef } from 'react';
import { InterviewDetailsAPI } from '@/components/shared/interview-scheduler/SchedulerDetails/api/InterviewDetailsAPI';

// Constants for polling configuration
const POLL_INTERVAL = 2000; // Polling interval in milliseconds (2 seconds)
const MAX_RETRIES = 5; // Maximum retries for initial fetch
const MAX_POLL_ATTEMPTS = 10; // Maximum polling attempts

// Type definitions
interface WeekRange {
  endStr: string;
  startStr: string;
  timeZone: string;
}

interface Interviewer {
  id?: string;
  avatar: string;
  name: string;
}

interface InterviewPlan {
  interviewers?: Interviewer[];
  interviewers1?: Interviewer[];
}

interface SuggestedUuidResponse {
  fetch_uuid: string;
}

interface PollResponse {
  status: 'PENDING' | 'SUCCESS';
  results?: {
    suggested_interviewer: Interviewer | null;
  };
}

const errorsMap = {
  err_invalid_interview_ats_job_stage_action: 'Invalid job stage action',
  err_missing_job_stage_action: 'Invalid job stage action',
};

// Add a cache to store previous results
const resultCache = new Map<string, PollResponse>();

export const useSchInterviewerLogic = ({
  enableRecommendation = false,
  interviewPlan,
  setValue,
  jobStageActivityId,
  datesSet = { startStr: '', endStr: '', timeZone: '' },
  bookedSlot = { start: '', end: '' },
}: {
  enableRecommendation?: boolean;
  interviewPlan?: InterviewPlan;
  setValue: (id: string) => void;
  jobStageActivityId?: number;
  datesSet?: WeekRange;
  bookedSlot?: { start: string; end: string };
}) => {
  const [isPollingActive, setIsPollingActive] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [recommendedUser, setRecommendedUser] = useState<
    Interviewer | 'no_interviewer_recommended'
  >(undefined);
  const toast = useShowToast();
  const activePollRef = useRef<string | null>(null);

  // Track whether the query has been initiated
  const [isQueryInitiated, setIsQueryInitiated] = useState(false);

  // Track current payload to detect real changes
  const currentPayloadRef = useRef<string>('');

  const interviewersFromTemplate = useMemo(
    () => interviewPlan?.interviewers || [],
    [interviewPlan]
  );
  const templateInterviewersCount = interviewersFromTemplate.length;

  /*
    New mode cases:
    Case 1: Exactly one user from template - load it directly
    Case 2: No users from template - no interviewers added
    Case 3: More than one user from template - fetch recommended via API
      3.1: API returns users - show recommended user
      3.2: API returns no users - show "could not find recommendation"
      3.3: API error - show error
    Edit mode:
      Skip all logic as it's pre-populated
  */

  const isPastTime = useMemo(() => {
    if (
      moment
        .tz(datesSet.startStr, datesSet?.timeZone)
        .isSame(moment.tz(datesSet?.timeZone).startOf('week'), 'week')
    ) {
      return bookedSlot?.start
        ? moment
            .tz(bookedSlot.start, datesSet?.timeZone)
            .isBefore(moment.tz(datesSet?.timeZone))
        : false;
    } else {
      return moment
        .tz(datesSet.startStr, datesSet?.timeZone)
        .isBefore(moment.tz(datesSet?.timeZone));
    }
  }, [datesSet, bookedSlot]);

  // Fix: Stabilize datesSet input with useMemo
  const stableDatesSet = useMemo(
    () => ({
      startStr: datesSet.startStr,
      endStr: datesSet.endStr,
      timeZone: datesSet.timeZone,
    }),
    [datesSet.startStr, datesSet.endStr, datesSet.timeZone]
  );

  const stableNow = useMemo(() => {
    return moment.tz(stableDatesSet.timeZone).toISOString();
  }, [stableDatesSet.timeZone]);

  const stableBookedSlot = useMemo(
    () => ({
      start: bookedSlot?.start || '',
      end: bookedSlot?.end || '',
    }),
    [bookedSlot?.start, bookedSlot?.end]
  );

  // Generate payload data for API calls
  const payloadData = useMemo(() => {
    if (!stableDatesSet.startStr || !stableDatesSet.endStr) {
      return null;
    }

    const isTimeSlot = Boolean(stableBookedSlot.start);
    const momentStart = moment.tz(
      stableDatesSet.startStr,
      stableDatesSet.timeZone
    );
    const now = moment.tz(stableDatesSet.timeZone);
    let interviewStart;

    if (isTimeSlot) {
      interviewStart = stableBookedSlot.start;
    } else {
      if (momentStart.isBefore(now)) {
        // datesSet.startStr is in the past, use current time
        interviewStart = stableNow;
      } else {
        // datesSet.startStr is in the future or present, use it
        interviewStart = stableDatesSet.startStr;
      }
    }

    return {
      ats_job_stage_action_id: jobStageActivityId,
      interview_start: isTimeSlot ? stableBookedSlot.start : interviewStart,
      interview_end: isTimeSlot ? stableBookedSlot.end : stableDatesSet.endStr,
      is_timeslot: isTimeSlot,
    };
  }, [jobStageActivityId, stableDatesSet, stableBookedSlot, stableNow]);

  // Generate a unique cache key based on payload
  const getCacheKey = useMemo(() => {
    return payloadData ? JSON.stringify(payloadData) : '';
  }, [payloadData]);

  // Handle payload changes (including booked slot changes)
  useEffect(() => {
    if (!getCacheKey) return;

    // If payload actually changed
    if (currentPayloadRef.current !== getCacheKey) {
      // Cancel any active polling
      if (activePollRef.current) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing; behavior preserved (extraction move)
        setIsPollingActive(false);
        setPollAttempts(0);
        activePollRef.current = null;
      }

      // Reset query initiated state to force a new query
      setIsQueryInitiated(false);

      // Update current payload reference
      currentPayloadRef.current = getCacheKey;

      // Check cache for this payload
      const cachedResult = resultCache.get(getCacheKey);
      if (cachedResult?.status === 'SUCCESS' && cachedResult.results) {
        setRecommendedUser(cachedResult.results.suggested_interviewer);
      }
    }
  }, [getCacheKey]);

  // Query to get suggested interviewer UUID
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- pre-existing; behavior preserved (extraction move)
  const { isFetching: isFetchingUuid } = useQuery({
    queryKey: ['suggestedUuid', getCacheKey],
    queryFn: async (): Promise<SuggestedUuidResponse> => {
      if (!payloadData) {
        throw new Error('Missing payload data');
      }

      const cachedResult = resultCache.get(getCacheKey);
      if (cachedResult?.status === 'SUCCESS' && cachedResult.results) {
        setRecommendedUser(cachedResult.results.suggested_interviewer);
        return Promise.resolve({ fetch_uuid: '' });
      }

      const response =
        await InterviewDetailsAPI.getSuggestedInterviewerId(payloadData);

      // @ts-expect-error -- pre-existing type debt (extraction move)
      const { data, errors } = response ?? {};
      if (errors?.length > 0) {
        toast({
          title: errorsMap?.[errors?.[0]] || 'Something went wrong',
          status: 'error',
        });
        return;
      }

      if (data) {
        handleInterviewerLogic(data);
      }
      setIsQueryInitiated(true);

      return response?.data;
    },
    enabled:
      enableRecommendation &&
      templateInterviewersCount > 1 &&
      !!stableDatesSet.startStr &&
      !isPastTime &&
      !isQueryInitiated &&
      !!payloadData,
    retry: MAX_RETRIES,
    staleTime: Infinity,
  });

  // Mutation to poll suggested interviewers
  const pollMutation = useMutation({
    mutationFn: async ({
      fetch_uuid,
    }: {
      fetch_uuid: string;
    }): Promise<PollResponse> => {
      if (!fetch_uuid) throw new Error('Missing fetch_uuid');
      return InterviewDetailsAPI.getPollSuggestedInterviewers({
        uuId: fetch_uuid,
      });
    },
    retry: MAX_RETRIES,
    onMutate: ({ fetch_uuid }) => {
      if (activePollRef.current && activePollRef.current !== fetch_uuid) {
        setIsPollingActive(false);
        setPollAttempts(0);
      }
      activePollRef.current = fetch_uuid;
      setIsPollingActive(true);
    },
    onSuccess: (response: PollResponse, variables: { fetch_uuid: string }) => {
      // Only process if this is still the active poll
      if (activePollRef.current !== variables.fetch_uuid) {
        return;
      }

      const { status, results } = response || {};

      // Store result in cache using current payload
      if (getCacheKey) {
        resultCache.set(getCacheKey, response);
      }

      if (status === 'PENDING') {
        if (pollAttempts >= MAX_POLL_ATTEMPTS) {
          toast({
            title: 'Unable to recommend interviewers',
            description:
              'No suggested interviewers are available at the moment.',
            status: 'error',
          });
          setRecommendedUser('no_interviewer_recommended');
          setIsPollingActive(false);
          setPollAttempts(0);
          activePollRef.current = null;
          return;
        }
        setPollAttempts((prev) => prev + 1);
        setTimeout(() => {
          // Only continue polling if this is still the active poll
          if (activePollRef.current === variables.fetch_uuid) {
            pollMutation.mutate({ fetch_uuid: variables.fetch_uuid });
          }
        }, POLL_INTERVAL);
      } else if (status === 'SUCCESS') {
        setIsPollingActive(false);
        setPollAttempts(0);
        activePollRef.current = null;
        setRecommendedUser(
          results?.suggested_interviewer || 'no_interviewer_recommended'
        );
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Unable to recommend interviewers',
        description: error?.message || 'Unknown error',
        status: 'error',
      });
      setRecommendedUser('no_interviewer_recommended');
      setIsPollingActive(false);
      setPollAttempts(0);
      activePollRef.current = null;
    },
  });

  // Handle logic based on mode and template interviewers count
  const handleInterviewerLogic = (uuidData) => {
    if (!enableRecommendation) {
      return;
    }

    // Cancel previous polling cycle before starting a new one
    if (
      activePollRef.current &&
      (!uuidData?.fetch_uuid || activePollRef.current !== uuidData?.fetch_uuid)
    ) {
      setIsPollingActive(false);
      setPollAttempts(0);
      activePollRef.current = null;
    }

    if (templateInterviewersCount === 1) {
      // Case 1: Single interviewer from template
      setValue(interviewersFromTemplate[0].id);
    } else if (templateInterviewersCount > 1 && uuidData?.fetch_uuid) {
      // Case 3: Multiple interviewers - start polling with UUID
      pollMutation.mutate({ fetch_uuid: uuidData?.fetch_uuid });
    }
    // Case 2: No interviewers - handled by default return state
  };

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      activePollRef.current = null;
      setIsPollingActive(false);
      setPollAttempts(0);
    };
  }, []);

  return {
    hide: templateInterviewersCount === 1,
    isLoading: isFetchingUuid || isPollingActive,
    recommendedUser: recommendedUser,
    isEmptyUsersFromTemplate: templateInterviewersCount === 0,
  };
};
